import { memo, useState, useRef } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Image, FileAudio, Video, FileText, Upload, X, Edit2, Trash2, Loader2, Copy, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, Button } from '@caruso/ui';
import { uploadService } from '@/services/upload.service';
import { toast } from 'sonner';

type MediaType = 'image' | 'audio' | 'video' | 'document';

const AVAILABLE_VARIABLES = [
  { key: 'nome', label: 'Nome', description: 'Nome do cliente' },
  { key: 'email', label: 'Email', description: 'Email do cliente' },
  { key: 'telefone', label: 'Telefone', description: 'Telefone do cliente' },
  { key: 'documento', label: 'Documento', description: 'CPF/CNPJ do cliente' },
  { key: 'endereco', label: 'Endereço', description: 'Endereço completo' },
  { key: 'cidade', label: 'Cidade', description: 'Cidade do cliente' },
  { key: 'estado', label: 'Estado', description: 'Estado/UF do cliente' },
  { key: 'cep', label: 'CEP', description: 'CEP do cliente' },
];

interface MediaData {
  mediaType: MediaType;
  fileName?: string;
  fileUrl?: string;
  caption?: string;
}

const mediaTypeConfig = {
  image: {
    icon: Image,
    label: 'Imagem',
    accept: 'image/*',
    color: 'text-blue-400',
  },
  audio: {
    icon: FileAudio,
    label: 'Áudio',
    accept: 'audio/*',
    color: 'text-green-400',
  },
  video: {
    icon: Video,
    label: 'Vídeo',
    accept: 'video/*',
    color: 'text-purple-400',
  },
  document: {
    icon: FileText,
    label: 'Documento',
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.txt',
    color: 'text-orange-400',
  },
};

export const MediaNode = memo(({ data, id }: NodeProps<MediaData>) => {
  const { deleteElements } = useReactFlow();
  const [isEditing, setIsEditing] = useState(false);
  const [caption, setCaption] = useState(data.caption || '');
  const [fileName, setFileName] = useState(data.fileName || '');
  const [fileUrl, setFileUrl] = useState(data.fileUrl || '');
  const [mediaType, setMediaType] = useState<MediaType>(data.mediaType || 'image');
  const [isUploading, setIsUploading] = useState(false);
  const [copiedVar, setCopiedVar] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const config = mediaTypeConfig[mediaType];

  const handleDelete = () => {
    deleteElements({ nodes: [{ id }] });
  };
  const Icon = config.icon;

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // Create local preview URL immediately
      const previewUrl = URL.createObjectURL(file);
      setFileUrl(previewUrl);
      setFileName(file.name);

      // Upload file to server
      const result = await uploadService.uploadMedia(file);

      // Replace preview URL with actual uploaded URL
      setFileUrl(result.url);
      setFileName(result.fileName);

      toast.success('Arquivo enviado com sucesso!');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erro ao enviar arquivo. Tente novamente.');

      // Clear on error
      setFileUrl('');
      setFileName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = () => {
    data.caption = caption;
    data.fileName = fileName;
    data.fileUrl = fileUrl;
    data.mediaType = mediaType;
    setIsEditing(false);
  };

  const handleCancel = () => {
    setCaption(data.caption || '');
    setFileName(data.fileName || '');
    setFileUrl(data.fileUrl || '');
    setMediaType(data.mediaType || 'image');
    setIsEditing(false);
  };

  const handleRemoveFile = () => {
    setFileName('');
    setFileUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const insertVariable = (varKey: string) => {
    const variable = `{{${varKey}}}`;
    setCaption(prev => prev + variable);
    setCopiedVar(varKey);
    setTimeout(() => setCopiedVar(null), 1500);
  };

  return (
    <>
      <div className="bg-neutral-900 border-2 border-brand-gold/40 rounded-lg p-4 min-w-[280px] shadow-lg">
        <Handle type="target" position={Position.Top} className="w-3 h-3 !bg-brand-gold" />

        <div className="flex items-center gap-2 mb-3">
          <Icon className={`w-5 h-5 ${config.color}`} />
          <span className="font-bold text-white">{config.label}</span>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setIsEditing(true)}
              className="text-brand-gold hover:text-white transition-colors p-1"
              title="Editar mídia"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              className="text-red-400 hover:text-red-300 transition-colors p-1"
              title="Deletar nó"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="space-y-2">
          {fileName ? (
            <>
              {fileUrl && mediaType === 'image' && (
                <div className="rounded overflow-hidden mb-2">
                  <img src={fileUrl} alt="Preview" className="w-full h-32 object-cover" />
                </div>
              )}
              <div className="flex items-center gap-2 p-2 bg-neutral-800 rounded">
                <Icon className="w-4 h-4 text-brand-gold" />
                <span className="flex-1 text-sm text-brand-ivory truncate">{fileName}</span>
              </div>
              {caption && (
                <div className="text-brand-ivory/80 text-sm whitespace-pre-wrap p-2 bg-neutral-800 rounded">
                  {caption}
                </div>
              )}
            </>
          ) : (
            <div className="text-brand-ivory/50 text-sm text-center py-3">
              Clique no ícone para adicionar arquivo
            </div>
          )}
        </div>

        <Handle type="source" position={Position.Bottom} className="w-3 h-3 !bg-brand-gold" />
      </div>

      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="bg-neutral-900 border-brand-gold/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Icon className={`w-5 h-5 ${config.color}`} />
              {config.label}
            </DialogTitle>
            <DialogDescription className="text-brand-ivory/70">
              Adicione um arquivo de mídia para enviar no fluxo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Media Type Selector */}
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">Tipo de Mídia</label>
              <select
                value={mediaType}
                onChange={(e) => setMediaType(e.target.value as MediaType)}
                className="w-full p-3 bg-neutral-800/50 border-neutral-700 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold/50"
              >
                {Object.entries(mediaTypeConfig).map(([type, config]) => (
                  <option key={type} value={type} className="bg-neutral-900">
                    {config.label}
                  </option>
                ))}
              </select>
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-brand-ivory mb-2">Arquivo</label>
              <input
                ref={fileInputRef}
                type="file"
                accept={config.accept}
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />

              {!fileName ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="w-full p-4 bg-neutral-800 border-2 border-dashed border-brand-gold/40 rounded-lg text-brand-gold hover:bg-neutral-800/80 flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm font-medium">Enviando...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-5 h-5" />
                      <span className="text-sm font-medium">Selecionar arquivo</span>
                    </>
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-2 p-3 bg-neutral-800/50 border border-neutral-700 rounded-lg">
                  {isUploading ? (
                    <Loader2 className="w-5 h-5 text-brand-gold animate-spin" />
                  ) : (
                    <Icon className="w-5 h-5 text-brand-gold" />
                  )}
                  <span className="flex-1 text-sm text-white truncate">{fileName}</span>
                  {!isUploading && (
                    <button
                      onClick={handleRemoveFile}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Preview */}
            {fileUrl && mediaType === 'image' && (
              <div className="rounded-lg overflow-hidden border border-neutral-700">
                <img src={fileUrl} alt="Preview" className="w-full h-40 object-cover" />
              </div>
            )}

            {/* Caption with Variables */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-brand-ivory">Legenda (opcional)</label>
              <p className="text-xs text-neutral-400">
                Use variáveis como <code className="px-1 py-0.5 bg-black/30 rounded text-brand-gold/80">{'{{nome}}'}</code> para personalizar
              </p>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                className="w-full p-3 bg-neutral-800/50 border border-neutral-700 text-white placeholder:text-neutral-500 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brand-gold/50 font-mono text-sm"
                rows={3}
                placeholder="Olá {{nome}}! Confira este conteúdo..."
              />

              {/* Variables Quick Insert */}
              <div className="flex flex-wrap gap-1">
                {AVAILABLE_VARIABLES.map((variable) => (
                  <button
                    key={variable.key}
                    onClick={() => insertVariable(variable.key)}
                    className="px-2 py-1 bg-neutral-800/50 hover:bg-neutral-800 border border-neutral-700 hover:border-brand-gold/50 rounded text-xs text-white transition-colors flex items-center gap-1"
                    title={variable.description}
                  >
                    {copiedVar === variable.key ? (
                      <Check className="w-3 h-3 text-green-400" />
                    ) : (
                      <Copy className="w-3 h-3 text-neutral-500" />
                    )}
                    {variable.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2 border-t border-neutral-700">
              <Button
                onClick={handleCancel}
                variant="outline"
                className="border-neutral-700 text-white hover:bg-neutral-800"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                className="bg-brand-gold hover:bg-brand-gold/90 text-brand-ink font-semibold"
              >
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});

MediaNode.displayName = 'MediaNode';
